import { AttributeEditorDialogViewModel } from '@Alternatives/Attributes/Components/AttributeEditorDialog';
import AttributeEditorDialog from '@Alternatives/Attributes/Components/AttributeEditorDialog.vue';
import AttributesQueries from '@Alternatives/Attributes/Domain/queries-vue-component';
import { AttributeEditorReadModel, AttributeReadModel } from '@Alternatives/Attributes/Domain/readmodels';
import { RealDefinedValue } from "@Alternatives/Model";
import { isNullOrWhitespace, strCompareLowerCase } from "@common/Common";
import GoalNavigation from '@common/Components/GoalNavigation.vue';
import PortfolioOrDecisionNavigation from "@common/Components/PortfolioOrDecisionNavigation.vue";
import { dynamicUiRoutes } from '@common/dynamicUiRoutes';
import { ICriteriaCommand } from '@Criteria/cqrs';
import { CriterionSelectorDialogViewModel } from '@Criteria/Hierarchies/Components/CriterionSelectorDialog';
import CriterionSelectorDialog from '@Criteria/Hierarchies/Components/CriterionSelectorDialog.vue';
import { UpdateCriterionMeasurementCommandV1 } from '@Criteria/Hierarchies/Domain/commands-core';
import CriteriaHierarchyCommands from "@Criteria/Hierarchies/Domain/commands-vue-component";
import CriteriaHierarchyQueries from "@Criteria/Hierarchies/Domain/queries-vue-component";
import { CriterionReadModel, MeasurementsPageReadModel } from "@Criteria/Hierarchies/Domain/readmodels";
import { DiscreteScaleLevel } from '@Scales/Model';
import { Component, mixins } from 'vue-facing-decorator';


@Component({
    components: { PortfolioOrDecisionNavigation, GoalNavigation, AttributeEditorDialog, CriterionSelectorDialog }
})
export default class MeasurementsPage extends mixins(CriteriaHierarchyQueries, CriteriaHierarchyCommands, AttributesQueries)
{
    get workspaceId() { return Number.parseInt(this.$route.params.workspaceId); }
    get projectId() { return Number.parseInt(this.$route.params.projectId); }
    get evalModelId() { return Number.parseInt(this.$route.params.evalModelId); }

    rm: MeasurementsPageReadModel | null = null;

    Initialized: boolean = false;

    SearchPhrase: string | null = null;
    SearchAttributePhrase: string | null = null;
    filterByCriterionId: number | null = null;

    bottomCriteria: BottomCriterionViewModel[] = [];
    editedCriterion: EditedCriterionViewModel = null;
    attributeEditorDialog: AttributeEditorDialogViewModel = new AttributeEditorDialogViewModel();

    criterionEditorOpened: boolean = false;
    criterionSelectorDialog = new CriterionSelectorDialogViewModel();


    get AttributesSections(): any[]
    {
        let attributes = this.rm.Attributes.sort((a, b) => strCompareLowerCase(a.Name, b.Name));

        if (!isNullOrWhitespace(this.SearchAttributePhrase))
        {
            const phrase = this.SearchAttributePhrase.toLowerCase();
            attributes = attributes.filter(a => (a.Name + " " + a.Section).toLowerCase().indexOf(phrase) >= 0);
        }

        const sections = attributes.map(a => a.Section)
            .filter((v, index, names) => names.indexOf(v) === index);

        const view = sections.map(section =>
        {
            const sAttributes = attributes.filter(a => a.Section === section);
            return { Section: section, Attributes: sAttributes };
        }).sort((a, b) => strCompareLowerCase(a.Section, b.Section));

        return view;
    }

    get FilterByCriterion(): CriterionReadModel { return this.rm.Criteria.find(a => a.Id === this.filterByCriterionId) ?? null; }

    get BottomCriteriaView(): BottomCriterionViewModel[] 
    {
        let view = this.bottomCriteria.sort((a, b) => strCompareLowerCase(a.Criterion.Path, b.Criterion.Path));

        if (this.FilterByCriterion)
        {
            view = view.filter(a => a.Criterion.Path.toLowerCase().indexOf(this.FilterByCriterion.Path.toLowerCase()) >= 0);
        }

        if (!isNullOrWhitespace(this.SearchPhrase))
        {
            const phrase = this.SearchPhrase.toLowerCase();
            view = view.filter(a => (a.Criterion.Name + " " + a.Criterion.Path).toLowerCase().indexOf(phrase) >= 0);
        }

        return view;
    }


    url_EditWeightedScorePage(attributeId: number): string
    {
        return dynamicUiRoutes.Criteria_EditWeightedScorePage2(this.$route, this.workspaceId, this.projectId, attributeId);
    }

    changeCriterion(criterionId: number)
    {
        const c = this.rm.Criteria.find(a => a.Id === criterionId);

        if (c.ParentId == null)
        {
            this.filterByCriterionId = null;
        }
        else
        {
            this.filterByCriterionId = criterionId;
        }
    }

    showCriterionSelector()
    {
        this.criterionSelectorDialog.show(this.FilterByCriterion?.Id, this.rm.Criteria);
    }

    showCriterionEditor(criterionId: number)
    {
        const criterion = this.rm.Criteria.find(c => c.Id === criterionId);
        this.editedCriterion = new EditedCriterionViewModel(criterion, this.rm)
        this.criterionEditorOpened = true;
    }

    closeCriterionEditor()
    {
        this.criterionEditorOpened = false;
    }


    showAttributeEditor(attr: AttributeReadModel)
    {
        if (attr.IsWeightedScore === true)
        {
            location.href = this.url_EditWeightedScorePage(attr.Id);
        }
        else
        {
            this.attributeEditorDialog.showEditor(attr.Id, attr.Name);
            this.runAttributeEditorQueryV1(this.workspaceId, this.projectId, attr.Id);
        }
    }

    showAttributeCreator()
    {
        const scaleLevels: RealDefinedValue[] | null = this.editedCriterion.ScaleLevels?.map(a =>
        {
            const dv = new RealDefinedValue();
            dv.Description = a.Definition;
            dv.Name = a.Name;
            dv.Value = a.Value;
            return dv;
        }) ?? null;

        this.attributeEditorDialog.showCreator({
            defaultName: this.editedCriterion.Name,
            defaultType: "Real",
            defaultInputType: "List",
            defaultDescription: this.editedCriterion.Description,
            defaultSection: "Criteria",
            defaultMinValue: 0,
            defaultRealDefinedValues: scaleLevels,

            allowChangeAttributeType: false,
            allowMultipleValuesEnabled: false,
            allowInputTypeEvaluate: true,
        });

        this.runAttributeEditorQueryV1(this.workspaceId, this.projectId, null);
    }

    expandAll()
    {
        this.bottomCriteria.forEach(a => a.Expanded = true);
    }

    collapseAll()
    {
        this.bottomCriteria.forEach(a => a.Expanded = false);
    }


    numericAttributeCreated(attrId: number, maxValue: number | null)
    {
        this.runAttributeByIdQueryV1(this.workspaceId, this.projectId, this.rm.AlternativesListId, attrId);
        this.invokeUpdateCriterionMeasurementCommandV1(this.workspaceId, this.rm.CriteriaHierarchyId, this.editedCriterion.Id, attrId, maxValue);
    }

    numericAttributeUpdated(attrId: number)
    {
        this.runAttributeByIdQueryV1(this.workspaceId, this.projectId, this.rm.AlternativesListId, attrId);
    }



    handleUpdateCriterionMeasurementCommand()
    {
        this.invokeUpdateCriterionMeasurementCommandV1(this.workspaceId, this.rm.CriteriaHierarchyId, this.editedCriterion.Id, this.editedCriterion.AttributeId, this.editedCriterion.MaxValue);
    }

    mounted()
    {
        document.title = "Measurements | TransparentChoice";
        this.runMeasurementsPageQueryV1(this.workspaceId, this.projectId, this.evalModelId);
    }

    onSetModel(model: any, modelName: string) 
    {
        if (modelName == "MeasurementsPageReadModel")
        {
            this.rm = model as MeasurementsPageReadModel;

            this.bottomCriteria = this.rm.Criteria
                .filter(a => a.IsChildless)
                .map(a => new BottomCriterionViewModel(a.Id, this.rm));

            this.Initialized = true;
        }
        else if (modelName == "AttributeReadModel")
        {
            const rm = model as AttributeReadModel;
            if (rm.IsNumeric === false) return;
            this.rm.Attributes = this.rm.Attributes.filter(a => a.Id !== rm.Id);
            this.rm.Attributes.push(rm);
        }
        else if (modelName === "AttributeEditorReadModel")
        {
            const rm = model as AttributeEditorReadModel;
            this.attributeEditorDialog.setData(rm);
        }
    }

    onCommandSucceed(cmd: ICriteriaCommand, correlationId: string, result: any)
    {
        if (UpdateCriterionMeasurementCommandV1.matchType(cmd))
        {
            const cmd1 = cmd as UpdateCriterionMeasurementCommandV1;

            const criterion = this.rm.Criteria.find(c => c.Id === Number(correlationId));
            criterion.AttributeId = cmd1.AttributeId;
            criterion.MaxValue = cmd1.MaxValue;

            this.closeCriterionEditor();
        }
    }
}


class EditedCriterionViewModel
{
    root: MeasurementsPageReadModel = null;

    Id: number = null;
    Name: string = null;
    Description: string = null;
    Path: string = null;
    ScaleLevels: DiscreteScaleLevel[] = null;

    AttributeId: number | null = null;
    MaxValue: number | null = null;

    get attribute(): AttributeReadModel { return this.root.Attributes.find(a => a.Id === this.AttributeId) ?? null; }
    get attributeNameOrNone(): string { return this.attribute?.Name ?? "None"; }
    get defaultMax(): number | null { return this.attribute?.MaxValue ?? null; }

    constructor(rm: CriterionReadModel, root: MeasurementsPageReadModel)
    {
        this.root = root;

        this.Id = rm.Id;
        this.Name = rm.Name;
        this.Description = rm.Description;
        this.Path = rm.Path;
        this.ScaleLevels = rm.ScaleLevels;

        this.AttributeId = rm.AttributeId;
        this.MaxValue = rm.MaxValue;
    }

    unlink()
    {
        this.AttributeId = null;
        this.MaxValue = null;
    }

    link(attrId: number)
    {
        this.AttributeId = attrId;
        this.MaxValue = null;
    }
}


class BottomCriterionViewModel
{
    root: MeasurementsPageReadModel;
    Id: number = null;
    Expanded: boolean = false;

    get Criterion(): CriterionReadModel { return this.root.Criteria.find(a => a.Id === this.Id); }
    get Attribute(): AttributeReadModel { return this.root.Attributes.find(a => a.Id === this.Criterion.AttributeId) ?? null; }
    get IsValid(): boolean { return this.Attribute != null && (this.Criterion.MaxValue != null || this.Attribute?.MaxValue != null); }

    constructor(criterionId: number, root: MeasurementsPageReadModel)
    {
        this.root = root;
        this.Id = criterionId;
    }
}